// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * The on-the-wire item envelope. Every beamed item — text, link, code, or an
 * image — is serialised into a single self-describing byte buffer, then encrypted
 * as one AES-GCM blob and framed as [iv(12) | ciphertext]. Keeping the framing
 * pure and versioned means it can be unit-tested end-to-end without any network
 * or crypto, and a future format bump is unambiguous.
 *
 * Plaintext layout:
 *   byte 0        version (1)
 *   bytes 1..4    uint32 BE header length
 *   bytes 5..     header JSON (UTF-8)
 *   rest          payload bytes (image data; empty for text/link/code)
 */
import type { ItemKind } from './types.ts';

export const ENVELOPE_VERSION = 1;
export const IV_LEN = 12;

/** The header carried alongside the payload — everything the receiver needs. */
export interface EnvHeader {
  id: string;
  kind: ItemKind;
  at: number;
  /** Text content for text / link / code items. */
  text?: string;
  /** Image filename. */
  name?: string;
  /** Image MIME type. */
  mime?: string;
}

const enc = new TextEncoder();
const dec = new TextDecoder();

/** Serialise a header + payload into the versioned plaintext envelope. */
export function encodeEnvelope(header: EnvHeader, payload: Uint8Array = new Uint8Array(0)): Uint8Array {
  const headerBytes = enc.encode(JSON.stringify(header));
  const out = new Uint8Array(5 + headerBytes.length + payload.length);
  const view = new DataView(out.buffer);
  out[0] = ENVELOPE_VERSION;
  view.setUint32(1, headerBytes.length, false);
  out.set(headerBytes, 5);
  out.set(payload, 5 + headerBytes.length);
  return out;
}

/** Parse a plaintext envelope. Throws on a truncated or wrong-version buffer. */
export function decodeEnvelope(bytes: Uint8Array): { header: EnvHeader; payload: Uint8Array } {
  if (bytes.length < 5) throw new Error('envelope too short');
  if (bytes[0] !== ENVELOPE_VERSION) throw new Error(`unsupported envelope version ${bytes[0]}`);
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const headerLen = view.getUint32(1, false);
  const headerEnd = 5 + headerLen;
  if (headerEnd > bytes.length) throw new Error('envelope header overruns buffer');
  const header = JSON.parse(dec.decode(bytes.subarray(5, headerEnd))) as EnvHeader;
  const payload = bytes.subarray(headerEnd);
  return { header, payload };
}

/** Prepend the 12-byte IV to the ciphertext to make the wire frame. */
export function packFrame(iv: Uint8Array, cipher: Uint8Array): Uint8Array {
  if (iv.length !== IV_LEN) throw new Error(`iv must be ${IV_LEN} bytes`);
  const frame = new Uint8Array(IV_LEN + cipher.length);
  frame.set(iv, 0);
  frame.set(cipher, IV_LEN);
  return frame;
}

/** Split a wire frame back into its IV and ciphertext. */
export function unpackFrame(frame: Uint8Array): { iv: Uint8Array; cipher: Uint8Array } {
  if (frame.length < IV_LEN) throw new Error('frame too short');
  return { iv: frame.subarray(0, IV_LEN), cipher: frame.subarray(IV_LEN) };
}
